import React, { useState, useRef, useEffect } from "react";
import "../../../../../css/RecruitmentScreen/Sections.css";
import UseDatepicker from "../Datepicker";
import SignatureCanvas from "react-signature-canvas";
import { writeDataS10 } from "../../../../../backend/Sections/Section10/writeDataS10";
import { readDataS10 } from "../../../../../backend/Sections/Section10/readDataS10";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import YesNoRadio from "../RadioButton";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";
import { shouldBypassValidation } from "../../Utils/validationPolicy";
import { validateSection10 } from "../Validation/section10";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import { formatDateValue } from "../Utils/dateFormatting";
import SearchInput from "../../../../DesignSystem/Inputs/SearchInput";
import {
  getAddressAutocomplete,
  getAddressById,
} from "../../Utils/Postcodes/getAddressClient";

const Section10 = ({ onValidationStatusChange }) => {
  const [formData, setFormData] = useState({
    unemployedOrClaimedBenefit: null,
    details: [
      {
        dwpOfficeAddress: "",
        dwpPostCode: "",
        name: "",
        currentAddress: "",
        postcode: "",
        benefitClaimed: "",
        nationalInsuranceNumber: "",
        dateOfBirth: "",
        from: "",
        to: "",
        benefitDetails: [
          {
            from: "",
            to: "",
            benefitClaimed: "",
          },
        ],
        areasOfConcern: "",
        officerSignature: "",
        advisorSignature: "",
        date: "",
      },
    ],
  });
  const [detailAddressSearch, setDetailAddressSearch] = useState({});
  const [detailAddrSuggestions, setDetailAddrSuggestions] = useState({});
  const [detailAddrDropdownOpen, setDetailAddrDropdownOpen] = useState({});
  const detailAddrDebounceRef = useRef(null);
  const detailAutoAbortRef = useRef(null);
  const detailGetAbortRef = useRef(null);

  const cancelAddressRequest = (ref) => {
    try {
      ref.current?.abort?.();
    } catch {}
  };

  useEffect(() => {
    return () => {
      cancelAddressRequest(detailAutoAbortRef);
      cancelAddressRequest(detailGetAbortRef);
      if (detailAddrDebounceRef.current) {
        clearTimeout(detailAddrDebounceRef.current);
      }
    };
  }, []);

  const runDetailAutocomplete = async (term, key) => {
    const q = String(term || "").trim();

    if (q.length < 2) {
      setDetailAddrSuggestions((prev) => ({ ...prev, [key]: [] }));
      setDetailAddrDropdownOpen((prev) => ({ ...prev, [key]: false }));
      return;
    }

    cancelAddressRequest(detailAutoAbortRef);
    const ac = new AbortController();
    detailAutoAbortRef.current = ac;

    const out = await getAddressAutocomplete(q, { signal: ac.signal });

    if (out?.aborted || ac.signal.aborted) return;

    if (!out.ok) {
      setDetailAddrSuggestions((prev) => ({ ...prev, [key]: [] }));
      setDetailAddrDropdownOpen((prev) => ({ ...prev, [key]: false }));
      return;
    }

    const list = out.suggestions || [];

    setDetailAddrSuggestions((prev) => ({
      ...prev,
      [key]: list,
    }));

    setDetailAddrDropdownOpen((prev) => ({
      ...prev,
      [key]: list.length > 0,
    }));
  };

  const applyDetailAddressFromId = async (id, index, type) => {
    cancelAddressRequest(detailGetAbortRef);
    const ac = new AbortController();
    detailGetAbortRef.current = ac;

    const out = await getAddressById(id, { signal: ac.signal });

    if (!out.ok) return;

    const a = out.address || {};

    setFormData((prevData) => {
      const updatedDetails = prevData.details.map((detail, i) => {
        if (i !== index) return detail;

        if (type === "dwp") {
          return {
            ...detail,
            dwpOfficeAddress: [a.address || a.line_1 || "", a.line_2 || ""]
              .filter(Boolean)
              .join(", "),
            dwpPostCode: a.postcode || "",
          };
        }

        return {
          ...detail,
          currentAddress: [a.address || a.line_1 || "", a.line_2 || ""]
            .filter(Boolean)
            .join(", "),
          postcode: a.postcode || "",
        };
      });

      return {
        ...prevData,
        details: updatedDetails,
      };
    });

    const key = `${type}-${index}`;

    setDetailAddressSearch((prev) => ({
      ...prev,
      [key]: a.postcode || a.address || a.line_1 || "",
    }));

    setDetailAddrSuggestions((prev) => ({
      ...prev,
      [key]: [],
    }));

    setDetailAddrDropdownOpen((prev) => ({
      ...prev,
      [key]: false,
    }));
  };

  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  useEffect(() => {
    const removeListener = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => removeListener();
  }, []);

  const addFormSection = () => {
    setFormData((prevData) => ({
      ...prevData, // Spread the existing formData object
      details: [
        ...prevData.details, // Spread the current details array
        {
          dwpOfficeAddress: "",
          dwpPostCode: "",
          name: "",
          currentAddress: "",
          postcode: "",
          benefitClaimed: "",
          nationalInsuranceNumber: "",
          dateOfBirth: "",
          from: "",
          to: "",
          benefitDetails: [
            {
              from: "",
              to: "",
              benefitClaimed: "",
            },
          ],
          areasOfConcern: "",
          officerSignature: "",
          advisorSignature: "",
          date: "",
        },
      ], // Add a new section to the details array
    }));
  };

  const officerSigCanvasRef = useRef([]);
  const advisorSigCanvasRef = useRef([]);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const sectionTopRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};

    if (formData.unemployedOrClaimedBenefit === null) {
      newErrors["unemployedOrClaimedBenefit"] = "This field is required";
    }
    if (formData.unemployedOrClaimedBenefit === true) {
      formData.details.forEach((section, index) => {
        if (section.dwpOfficeAddress.trim() === "")
          newErrors[`dwpOfficeAddress-${index}`] = "This field is required";
        if (section.dwpPostCode.trim() === "")
          newErrors[`dwpPostCode-${index}`] = "This field is required";
        if (section.name.trim() === "")
          newErrors[`name-${index}`] = "This field is required";
        if (section.currentAddress.trim() === "")
          newErrors[`currentAddress-${index}`] = "This field is required";
        if (section.postcode.trim() === "")
          newErrors[`postcode-${index}`] = "This field is required";
        if (!section.benefitClaimed)
          newErrors[`benefitClaimed-${index}`] = "This field is required";
        if (section.nationalInsuranceNumber.trim() === "")
          newErrors[`nationalInsuranceNumber-${index}`] =
            "This field is required";
        if (!section.dateOfBirth)
          newErrors[`dateOfBirth-${index}`] = "This field is required";
        if (!section.from)
          newErrors[`from-${index}`] = "This field is required";
        if (!section.to) newErrors[`to-${index}`] = "This field is required";
        if (section.areasOfConcern.trim() === "")
          newErrors[`areasOfConcern-${index}`] = "This field is required";
        if (!section.date)
          newErrors[`date-${index}`] = "This field is required";
        // if (!section.officerSignature)
        //   newErrors[`officerSignature-${index}`] = "This field is required";
        if (!section.advisorSignature)
          newErrors[`advisorSignature-${index}`] = "This field is required";

        section.benefitDetails.forEach((benefit, benefitIndex) => {
          if (!benefit.from)
            newErrors[`from-${index}-${benefitIndex}`] =
              "This field is required";
          if (!benefit.to)
            newErrors[`to-${index}-${benefitIndex}`] = "This field is required";
          if (benefit.benefitClaimed.trim() === "")
            newErrors[`benefitClaimed-${index}-${benefitIndex}`] =
              "This field is required";
        });
      });
    }

    setErrors(newErrors);
    errorTrackerV2.setSectionErrors("section10", newErrors);
    onValidationStatusChange(Object.keys(newErrors).length === 0);
    return Object.keys(newErrors).length === 0; // If there are no errors, return true
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await readDataS10(); // Fetch the data from Firestore

        // Ensure that data is not null or undefined
        if (data) {
          // Convert object with numeric keys to array
          const dataArray = Object.keys(data.details).map((key) => {
            const section = data.details[key];

            // Convert Firestore Timestamps to Date objects
            const updatedBenefitDetails = section.benefitDetails.map(
              (benefit) => ({
                ...benefit,
                from: benefit.from ? new Date(benefit.from.seconds * 1000) : "",
                to: benefit.to ? new Date(benefit.to.seconds * 1000) : "",
              }),
            );

            return {
              ...section,
              from: section.from ? new Date(section.from.seconds * 1000) : "",
              to: section.to ? new Date(section.to.seconds * 1000) : "",
              benefitDetails: updatedBenefitDetails,
              date: section.date ? new Date(section.date.seconds * 1000) : "",
              dateOfBirth: section.dateOfBirth
                ? new Date(section.dateOfBirth.seconds * 1000)
                : "",
            };
          });

          // Update the `details` array within `formData`
          setFormData((prevData) => ({
            ...prevData,
            unemployedOrClaimedBenefit: data.unemployedOrClaimedBenefit,
            details: dataArray, // Only update the `details` array
          }));

          const searchMap = {};

          dataArray.forEach((section, index) => {
            searchMap[`dwp-${index}`] =
              section.dwpPostCode || section.dwpOfficeAddress || "";
            searchMap[`current-${index}`] =
              section.postcode || section.currentAddress || "";
          });

          setDetailAddressSearch(searchMap);

          // onValidationStatusChange(true);
        } else {
          // console.warn("No valid data found for Section 10.");
        }
      } catch (error) {
        // console.error("Error fetching Section 10 data:", error);
      }
    };

    fetchData();
    validateForm();
  }, []);

  useEffect(() => {
    formData.details.forEach((section, index) => {
      if (section.advisorSignature && advisorSigCanvasRef.current[index]) {
        advisorSigCanvasRef.current[index].fromDataURL(
          section.advisorSignature,
        );
      }
    });
  }, [formData.details]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });

    setShowErrors(true);

    const ok = validateSection10({
      formData,
      setErrors,
      onValidationStatusChange,
    });

    // Save data regardless of validation status
    await writeDataS10(formData);

    // Show appropriate alert based on validation result
    if (ok || shouldBypassValidation(auth.currentUser?.uid)) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  const clearSignaturePad = (index, sigCanvasRefs, fieldName) => {
    // Clear the signature canvas for the given index
    sigCanvasRefs.current[index].clear();

    // Update the form data state to clear the corresponding field
    setFormData((prevData) => {
      // Create a deep copy of the details array to avoid mutating state directly
      const updatedDetails = [...prevData.details];

      // Update the specific field (e.g., officerSignature or advisorSignature) to an empty string
      updatedDetails[index] = {
        ...updatedDetails[index],
        [fieldName]: "", // Clear the signature in the form data
      };

      return {
        ...prevData,
        details: updatedDetails, // Update the details array in the form data state
      };
    });
  };

  const saveSignature = (index, sigCanvasRefs, fieldName) => {
    const signatureDataUrl = sigCanvasRefs.current[index].toDataURL(); // Convert signature to base64

    setFormData((prevData) => {
      // Create a deep copy of the formData to prevent mutation
      const updatedDetails = [...prevData.details];

      // Update the specific field (e.g., advisorSignature or officerSignature) for the corresponding index
      updatedDetails[index] = {
        ...updatedDetails[index],
        [fieldName]: signatureDataUrl, // Save signature in form data
      };

      return {
        ...prevData,
        details: updatedDetails, // Update the details array in formData state
      };
    });
  };

  const handleAddRow = (index) => {
    setFormData((prevData) => {
      // Map over the previous details array to create a new array with the updated benefitDetails
      const updatedDetails = prevData.details.map((detail, i) => {
        if (i === index) {
          // For the selected index, create a new benefitDetails array with an additional row
          return {
            ...detail,
            benefitDetails: [
              ...detail.benefitDetails,
              {
                from: "",
                to: "",
                benefitClaimed: "",
              },
            ],
          };
        }
        return detail; // Return the unchanged details
      });

      return { ...prevData, details: updatedDetails }; // Update the details array in the formData state
    });
  };

  // Remove the last row from benefitDetails for a specific section (form index)
  const handleRemoveLastRow = (index) => {
    setFormData((prevData) => {
      // Map over the details array to create a new array with the updated benefitDetails
      const updatedDetails = prevData.details.map((detail, i) => {
        if (i === index && detail.benefitDetails.length > 1) {
          // For the selected index, create a new benefitDetails array by removing the last row
          return {
            ...detail,
            benefitDetails: detail.benefitDetails.slice(0, -1), // Remove the last row using slice
          };
        }
        return detail; // Return the unchanged details
      });

      return { ...prevData, details: updatedDetails }; // Update the details array in the formData state
    });
  };

  const handleChange = (index, e) => {
    // Handle both synthetic events and native events
    const name = e?.target?.name || "";
    const value = e?.target?.value || "";

    setFormData((prevData) => {
      const updatedDetails = prevData.details.map((detail, i) =>
        i === index ? { ...detail, [name]: value } : detail,
      );

      return {
        ...prevData,
        details: updatedDetails,
      };
    });

    // Set the error state accordingly
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${name}-${index}`]: value ? "" : "This field is required",
    }));
  };

  // Proper handler for DateField onChange events
  const handleDateChange = (index, field, event) => {
    // Extract the actual value from the event (YYYY-MM-DD format)
    const dateString = event?.target?.value || "";
    // Convert string to Date object for consistency with Firestore data
    const dateValue = dateString ? new Date(dateString) : "";

    setFormData((prevData) => {
      const updatedDetails = prevData.details.map((detail, i) =>
        i === index ? { ...detail, [field]: dateValue } : detail,
      );

      return {
        ...prevData,
        details: updatedDetails,
      };
    });

    // Update error state
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${field}-${index}`]: dateValue ? "" : "This field is required",
    }));
  };

  const deleteLastSection = () => {
    if (formData.details.length > 1) {
      const updatedDetails = formData.details.slice(0, -1); // Remove the last section
      setFormData((prevData) => ({
        ...prevData, // Spread the existing formData object
        details: updatedDetails, // Update the details array
      }));
    }
  };

  // Handle date changes for UseDatepicker
  // const handleDateChange = (index, field, date) => {
  //   setFormData((prevData) => {
  //     const updatedDetails = prevData.details.map((detail, i) =>
  //       i === index ? { ...detail, [field]: date } : detail,
  //     );

  //     return {
  //       ...prevData,
  //       details: updatedDetails,
  //     };
  //   });

  //   // Update the error state accordingly
  //   setErrors((prevErrors) => ({
  //     ...prevErrors,
  //     [`${field}-${index}`]: date ? "" : "This field is required",
  //   }));
  // };

  const handleBenefitDetailsChange = (
    formIndex,
    benefitIndex,
    field,
    value,
  ) => {
    // Convert date strings to Date objects if this is a date field
    let processedValue = value;
    if (
      (field === "from" || field === "to") &&
      typeof value === "string" &&
      value
    ) {
      processedValue = new Date(value);
    }

    // Create a new updatedDetails array by mapping over the current details
    setFormData((prevData) => {
      const updatedDetails = prevData.details.map((detail, i) => {
        if (i === formIndex) {
          // For the selected formIndex, update the benefitDetails
          const updatedBenefitDetails = detail.benefitDetails.map(
            (benefit, j) => {
              // For the selected benefitIndex, update the field
              if (j === benefitIndex) {
                return { ...benefit, [field]: processedValue };
              }
              return benefit;
            },
          );
          return { ...detail, benefitDetails: updatedBenefitDetails };
        }
        return detail; // Return the unchanged details
      });

      return { ...prevData, details: updatedDetails };
    });

    // Update the error state accordingly
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${field}-${formIndex}-${benefitIndex}`]: processedValue
        ? ""
        : "This field is required",
    }));
  };

  

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onDismiss={() => setAlertMessage("")}
        />
      )}
      <h3>Section 10 – Unemployment Details</h3>
      <div className="Content ">
        If you have been unemployed or claiming any kind of benefit over the
        last three years please complete the section below, which will be sent
        to the DWP if appropriate. If you have been claiming benefit at more
        than one office, please duplicate this page the relevant number of times
        (once for each benefit office). All boxes should be completed by you;
        please ensure that you sign at the bottom of each sheet.
      </div>
      <div className="input-group-row">
        <div className="Content">
          Have you been unemployed or claiming any kind of benefit over the last
          3 years?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <YesNoRadio
          name="unemployedOrClaimedBenefit"
          value={formData.unemployedOrClaimedBenefit}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              unemployedOrClaimedBenefit:
                String(e.target.value).toLowerCase() === "true",
            }))
          }
          error={errors.unemployedOrClaimedBenefit}
        />
      </div>
      {formData.unemployedOrClaimedBenefit === true &&
        Array.isArray(formData.details) &&
        formData.details.map((data, index) => {
          const canAddBenefitRow = true; // you can add any time (or add your own cap)
          const canDeleteBenefitRow =
            Array.isArray(data.benefitDetails) &&
            data.benefitDetails.length > 1;

          return (
            <div key={index}>
              <div className="separator" />
              <div className="Content"> {index + 1}. DWP Office Address</div>

              <SearchInput
                label="DWP Office Address Lookup"
                placeholder="Start typing address or postcode..."
                value={detailAddressSearch[`dwp-${index}`] || ""}
                suggestions={
                  detailAddrDropdownOpen[`dwp-${index}`]
                    ? detailAddrSuggestions[`dwp-${index}`] || []
                    : []
                }
                onSuggestionSelect={(s) => {
                  const addressId =
                    s?.global_address_key ||
                    s?.id ||
                    s?.value ||
                    s?.addressId ||
                    null;

                  if (addressId) {
                    applyDetailAddressFromId(addressId, index, "dwp");
                  }
                }}
                onChange={(val) => {
                  const key = `dwp-${index}`;

                  setDetailAddressSearch((prev) => ({
                    ...prev,
                    [key]: val,
                  }));

                  if (detailAddrDebounceRef.current) {
                    clearTimeout(detailAddrDebounceRef.current);
                  }

                  detailAddrDebounceRef.current = setTimeout(
                    () => runDetailAutocomplete(val, key),
                    300,
                  );
                }}
                onSubmit={() =>
                  runDetailAutocomplete(
                    detailAddressSearch[`dwp-${index}`] || "",
                    `dwp-${index}`,
                  )
                }
              />
              <div className="sizebox" />

              <div className="input-group input-group--grid-2col">
                <div className="input-group">
                  <InputField
                    name="dwpOfficeAddress"
                    placeholder="DWP Office Address"
                    multiline
                    rows={3}
                    value={data.dwpOfficeAddress}
                    error={errors[`dwpOfficeAddress-${index}`]}
                    onChange={(e) => handleChange(index, e)}
                  />
                </div>
                <div className="input-group">
                  <InputField
                    name="dwpPostCode"
                    placeholder="DWP Postcode"
                    value={data.dwpPostCode}
                    error={errors[`dwpPostCode-${index}`]}
                    onChange={(e) => handleChange(index, e)}
                  />
                </div>
              </div>
              <div className="sizebox" />
              <SearchInput
                label="Current Address Lookup"
                placeholder="Start typing address or postcode..."
                value={detailAddressSearch[`current-${index}`] || ""}
                suggestions={
                  detailAddrDropdownOpen[`current-${index}`]
                    ? detailAddrSuggestions[`current-${index}`] || []
                    : []
                }
                onSuggestionSelect={(s) => {
                  const addressId =
                    s?.global_address_key ||
                    s?.id ||
                    s?.value ||
                    s?.addressId ||
                    null;

                  if (addressId) {
                    applyDetailAddressFromId(addressId, index, "current");
                  }
                }}
                onChange={(val) => {
                  const key = `current-${index}`;

                  setDetailAddressSearch((prev) => ({
                    ...prev,
                    [key]: val,
                  }));

                  if (detailAddrDebounceRef.current) {
                    clearTimeout(detailAddrDebounceRef.current);
                  }

                  detailAddrDebounceRef.current = setTimeout(
                    () => runDetailAutocomplete(val, key),
                    300,
                  );
                }}
                onSubmit={() =>
                  runDetailAutocomplete(
                    detailAddressSearch[`current-${index}`] || "",
                    `current-${index}`,
                  )
                }
              />
              <div className="sizebox" />
              <div className="form-row">
                <div className="input-group input-group--grid-2col">
                  <InputField
                    name="name"
                    placeholder="Name"
                    value={data.name}
                    error={errors[`name-${index}`]}
                    onChange={(e) => handleChange(index, e)}
                  />
                  <InputField
                    name="postcode"
                    placeholder="Postcode"
                    value={data.postcode}
                    error={errors[`postcode-${index}`]}
                    onChange={(e) => handleChange(index, e)}
                  />{" "}
                  <InputField
                    name="currentAddress"
                    placeholder="Current Address"
                    value={data.currentAddress}
                    error={errors[`currentAddress-${index}`]}
                    onChange={(e) => handleChange(index, e)}
                    multiline
                    rows={3}
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      width: "100%",
                    }}
                  >
                    <InputField
                      name="benefitClaimed"
                      placeholder="Type of benefit claimed"
                      value={data.benefitClaimed}
                      error={errors[`benefitClaimed-${index}`]}
                      onChange={(e) => handleChange(index, e)}
                    />
                    <InputField
                      name="nationalInsuranceNumber"
                      placeholder="NI Number"
                      value={data.nationalInsuranceNumber}
                      error={errors[`nationalInsuranceNumber-${index}`]}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </div>
                </div>
                <div className="input-group input-group--grid-2col">
                  <DateField
                    name="dateOfBirth"
                    label="Date of Birth"
                    required
                    dateFormat={"dd/MM/yyyy"}
                    value={data.dateOfBirth}
                    selected={data.dateOfBirth}
                    error={showErrors && !!errors[`dateOfBirth-${index}`]}
                    onChange={(event) =>
                      handleDateChange(index, "dateOfBirth", event)
                    }
                  />

                  <DateField
                    name="from"
                    required
                    label="From"
                    dateFormat={"dd/MM/yyyy"}
                    value={data.from} // Pass the selected date
                    onChange={(event) => handleDateChange(index, "from", event)}
                    error={showErrors && !!errors[`from-${index}`]}
                  />

                  <DateField
                    name="to"
                    required
                    label="To"
                    value={data.to} // Pass the selected date
                    dateFormat={"dd/MM/yyyy"}
                    error={showErrors && !!errors[`to-${index}`]}
                    onChange={(event) => handleDateChange(index, "to", event)}
                  />
                </div>
              </div>
              <div className="separator" />
              <div className="Content">
                The above named has applied to this company for a contract and
                has given their authority for us to approach you in respect of
                their benefit claims. We would therefore be obliged if you could
                provide the following information:
              </div>
              <div className="sizebox" />
              <div className="complaint-table-container">
                <table className="complaint-table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Type of Benefit Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(data.benefitDetails) &&
                      data.benefitDetails.map((benefit, i) => (
                        <tr key={i}>
                          <td>
                            <div className="form-row">
                              <DateField
                                name="fromBenefit"
                                placeholderText="From"
                                value={benefit.from} // Pass the selected date
                                error={
                                  showErrors && !!errors[`from-${index}-${i}`]
                                } // Pass any errors
                                onChange={(event) =>
                                  handleBenefitDetailsChange(
                                    index,
                                    i,
                                    "from",
                                    event?.target?.value || "",
                                  )
                                } // Update the form state
                              />
                            </div>
                          </td>

                          <td>
                            <div className="form-row">
                              <DateField
                                name="to"
                                placeholderText="To"
                                error={
                                  showErrors && !!errors[`to-${index}-${i}`]
                                } // Pass any errors
                                value={benefit.to} // Pass the selected date
                                onChange={(event) =>
                                  handleBenefitDetailsChange(
                                    index,
                                    i,
                                    "to",
                                    event?.target?.value || "",
                                  )
                                } // Update the form state
                              />
                            </div>
                          </td>
                          <td>
                            {/* <div className="small-size-box" /> */}
                            <InputField
                              name="benefitClaimed"
                              placeholder="Type"
                              error={errors[`benefitClaimed-${index}-${i}`]}
                              value={benefit.benefitClaimed}
                              onChange={(e) =>
                                handleBenefitDetailsChange(
                                  index,
                                  i,
                                  "benefitClaimed",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="button-group">
                {canAddBenefitRow && (
                  <button
                    type="button"
                    className="icon-button add-button"
                    onClick={() => handleAddRow(index)}
                    title="Add benefit row"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                )}

                {canDeleteBenefitRow && (
                  <button
                    type="button"
                    className="icon-button delete-button"
                    onClick={() => handleRemoveLastRow(index)}
                    title="Delete last benefit row"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>

              <div className="sizebox" />
              <div className="input-group-row">
                <div className="Content">
                  Please advise if your records indicate any areas of concern
                  <span style={{ color: "var(--Error)" }}>*</span>
                </div>

                <div className="input-group">
                  <InputField
                    name="areasOfConcern"
                    placeholder="Areas of Concern"
                    value={data.areasOfConcern}
                    error={errors[`areasOfConcern-${index}`]}
                    onChange={(e) => handleChange(index, e)}
                  />
                </div>
              </div>
              <div className="Content-Small">
                Please return this letter in the enclosed pre-paid envelope as
                soon as possible
              </div>
              <div className="Content-Small">
                Thank you for your co-operation.
              </div>
              <div className="Content-Small">Yours faithfully,</div>

              <div className="input-group-row">
                <div className="Content">
                  Recruitment Administration Team Benefit Office
                  (Signature/Stamp)
                  {/* <span style={{ color: "var(--Error)" }}>*</span> */}
                </div>

                <div className="input-group">
                  <SignatureCanvas
                    ref={(el) => (officerSigCanvasRef.current[index] = el)}
                    penColor="#fffeea"
                    canvasProps={{
                      className: "sigCanvas",
                    }}
                    onEnd={
                      () => officerSigCanvasRef.current[index].clear()
                      // saveSignature(
                      //   index,
                      //   officerSigCanvasRef,
                      //   "officerSignature"
                      // )
                    }
                  />
                  {errors[`officerSignature-${index}`] && (
                    <span className="error-message">
                      {errors[`officerSignature-${index}`]}
                    </span>
                  )}
                  {/* <button
                type="button"
                onClick={() =>
                  clearSignaturePad(
                    index,
                    officerSigCanvasRef,
                    "officerSignature"
                  )
                }
              >
                Clear
              </button> */}
                </div>
              </div>
              <div className="Content-Small">
                I hereby authorise Rosemount Financial Solutions (IFA) Ltd to
                approach the Department of Work & Pensions for information in
                respect of any periods of unemployment, benefit claims etc. and
                I authorise the Department to provide this information.
              </div>
              <div className="input-group-row">
                <div className="Content">
                  Signature<span style={{ color: "var(--Error)" }}>*</span>
                </div>
                <div className="form-row">
                  <SignatureCanvas
                    ref={(el) => (advisorSigCanvasRef.current[index] = el)}
                    penColor="#890304"
                    canvasProps={{
                      className: "sigCanvas",
                    }}
                    onEnd={() =>
                      saveSignature(
                        index,
                        advisorSigCanvasRef,
                        "advisorSignature",
                      )
                    }
                  />

                  {/* {errors[`advisorSignature-${index}`] && (
                  <span className="error-message">
                    {errors[`advisorSignature-${index}`]}
                  </span>
                )} */}
                  <PrimaryButton
                    type="button"
                    label="Clear"
                    onClick={() =>
                      clearSignaturePad(
                        index,
                        advisorSigCanvasRef,
                        "advisorSignature",
                      )
                    }
                  >
                    Clear
                  </PrimaryButton>
                </div>
              </div>
              <div className="sizebox" />
              <div className="input-group-row">
                <div className="Content">
                  Date<span style={{ color: "var(--Error)" }}>*</span>
                </div>
                <div className="input-group">
                  <DateField
                    name="date"
                    dateFormat={"dd/MM/yyyy"}
                    value={data.date}
                    error={showErrors && !!errors[`date-${index}`]}
                    onChange={(event) => handleDateChange(index, "date", event)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      {formData.unemployedOrClaimedBenefit === true && (
        <div className="button-group">
          <button
            type="button"
            className="icon-button add-button"
            onClick={addFormSection}
            title="Add another benefit office"
          >
            <i className="fas fa-plus"></i>
          </button>

          {Array.isArray(formData.details) && formData.details.length > 1 && (
            <button
              type="button"
              className="icon-button delete-button"
              onClick={deleteLastSection}
              title="Delete last benefit office"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
      )}

      <div className="sizebox" />
      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section10;
